import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListIcon from '@mui/icons-material/List';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../../app/hooks';
import { useBlock } from '../../../../services/entities/hooks/useBlock';
import { UnitsEnum } from '../../../../services/entities/impl/blfApi/apiConstants';
import { useFeatureFlags } from '../../../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { flexCenter } from '../../../commonUI/cssClasses';
import { DiagnosticFeature } from '../../../commonUI/viewMode/DiagnosticFeature';
import {
  MeasurementTypes,
  METERS_TO_FEET_CONVERSION_FACTOR,
  useUnitSymbol,
} from '../../../commonUtils';
import {
  RanchPageViewEnum,
  selectRanchPageView,
} from '../../../ranches/ranchesSlice';
import { roundFractional } from '../../../ranches/ranchUtils';
import { useCalculateCoveredPercentOfBlock } from '../../../scanDataApi/hooks/useCalculateCoveredPercentOfBlock';
import { selectCurrBlockID } from '../../../select/selectSlice';
import { useUnits } from '../../../settings/hooks/useUnits';
import useMeasureAcres from './hooks/useMeasureAcres';

const roundDigits = 2;
export const acrePercentageDifferenceThreshold = 5;

const MapBlockInfo: React.FC = () => {
  const theme = useTheme();
  const units = useUnits();
  const { GeocoderTool } = useFeatureFlags();
  const currBlockID = useAppSelector(selectCurrBlockID);
  const ranchPageView = useAppSelector(selectRanchPageView);
  const acresResult = useMeasureAcres();
  const { data: coveredScanPercent } = useCalculateCoveredPercentOfBlock({});
  const {
    calculatedAcres,
    userInputAcres,
    userInputHectares,
    calculatedHectares,
    isLoading,
  } = acresResult;
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Fetch the block data from the API
  const { block: blockData } = useBlock(currBlockID);

  const handleChange = () => {
    setIsOpen((prev) => !prev);
  };

  const percentageDifference =
    userInputAcres && calculatedAcres
      ? (Math.abs(userInputAcres - calculatedAcres) / userInputAcres) * 100
      : null;

  // Collect errors for display
  const errors: string[] = useMemo(() => {
    const errorsBuffer = [];
    if (acresResult.hasError || !calculatedAcres) {
      errorsBuffer.push(t('Invalid Block', { ns: 'ranch-view' }));
      return errorsBuffer;
    }

    if (!blockData?.geoOrientation) {
      errorsBuffer.push(t('Missing Orientation', { ns: 'ranch-view' }));
    }

    if (!blockData?.plantSpacing) {
      errorsBuffer.push(t('Missing Plant Spacing', { ns: 'ranch-view' }));
    }

    if (!blockData?.rowSpacing) {
      errorsBuffer.push(t('Missing Row Spacing', { ns: 'ranch-view' }));
    }

    if (!userInputAcres) {
      errorsBuffer.push(
        units === UnitsEnum.imperial
          ? t('Missing Input Acres', { ns: 'ranch-view' })
          : t('Missing Input Hectares', { ns: 'ranch-view' })
      );
    }

    if (
      percentageDifference &&
      percentageDifference > acrePercentageDifferenceThreshold
    ) {
      errorsBuffer.push(
        units === UnitsEnum.imperial
          ? t('Large Acre Difference', { ns: 'ranch-view' })
          : t('Large Hectare Difference', { ns: 'ranch-view' })
      );
    }

    return errorsBuffer;
  }, [
    acresResult.hasError,
    blockData?.geoOrientation,
    blockData?.plantSpacing,
    blockData?.rowSpacing,
    calculatedAcres,
    percentageDifference,
    t,
    units,
    userInputAcres,
  ]);

  const errorsToDisplay = useMemo(() => {
    return (
      <>
        {errors.map((error, i) => (
          <AcreDisplayWithLabel key={i} label={error} value={null} hasError />
        ))}
      </>
    );
  }, [errors]);

  // If any errors, automatically open the accordion
  useEffect(() => {
    if (blockData && errors.length > 0) {
      console.log('errors', errors);
      setIsOpen(true);
    }
  }, [errors, blockData]);

  const content = useMemo(() => {
    // Only display acres if on the block detail page and if not loading
    if (ranchPageView !== RanchPageViewEnum.blockDetail || isLoading) {
      return null;
    }

    const spacingContent = (
      <>
        {blockData?.plantSpacing && (
          <SpacingDisplayWithLabel
            label={
              units === UnitsEnum.imperial
                ? t('Plant Spacing', { ns: 'ranch-view' })
                : t('Plant Spacing', { ns: 'ranch-view' })
            }
            value={
              units === UnitsEnum.imperial
                ? blockData?.plantSpacing || 0
                : (blockData?.plantSpacing || 0) /
                  METERS_TO_FEET_CONVERSION_FACTOR
            }
          />
        )}
        {blockData?.rowSpacing && (
          <SpacingDisplayWithLabel
            label={
              units === UnitsEnum.imperial
                ? t('Row Spacing', { ns: 'ranch-view' })
                : t('Row Spacing', { ns: 'ranch-view' })
            }
            value={
              units === UnitsEnum.imperial
                ? blockData?.rowSpacing || 0
                : (blockData?.rowSpacing || 0) /
                  METERS_TO_FEET_CONVERSION_FACTOR
            }
          />
        )}
      </>
    );

    // if difference is bigger than threshold, display calculated and user input
    if (
      percentageDifference &&
      percentageDifference > acrePercentageDifferenceThreshold
    ) {
      return (
        <>
          <AcreDisplayWithLabel
            label={
              units === UnitsEnum.imperial
                ? t('Calculated Acres', { ns: 'ranch-view' })
                : t('Calculated Hectares', { ns: 'ranch-view' })
            }
            value={
              units === UnitsEnum.imperial
                ? calculatedAcres
                : calculatedHectares
            }
            hasError
          />
          <AcreDisplayWithLabel
            label={
              units === UnitsEnum.imperial
                ? t('Input Acres', { ns: 'ranch-view' })
                : t('Input Hectares', { ns: 'ranch-view' })
            }
            value={
              units === UnitsEnum.imperial ? userInputAcres : userInputHectares
            }
            hasError
          />
          {spacingContent}
        </>
      );
    }

    return (
      <>
        <AcreDisplayWithLabel
          label={
            units === UnitsEnum.imperial
              ? t('Acres', { ns: 'ranch-view' })
              : t('Hectares', { ns: 'ranch-view' })
          }
          value={
            units === UnitsEnum.imperial ? calculatedAcres : calculatedHectares
          }
        />
        {coveredScanPercent && (
          <DiagnosticFeature>
            <AcreDisplayWithLabel
              label={t('% of Block covered by Scan', { ns: 'ranch-view' })}
              value={coveredScanPercent}
            />
          </DiagnosticFeature>
        )}
        {spacingContent}
      </>
    );
  }, [
    ranchPageView,
    isLoading,
    blockData?.plantSpacing,
    blockData?.rowSpacing,
    units,
    t,
    percentageDifference,
    calculatedAcres,
    calculatedHectares,
    coveredScanPercent,
    userInputAcres,
    userInputHectares,
  ]);

  return (
    content && (
      <Box
        sx={{
          position: 'absolute',
          top: GeocoderTool ? '60px' : '8px', // Position below geocoder only when GeocoderTool is enabled
          right: '3rem',
          opacity: 0.9,
          zIndex: 2,
        }}
      >
        <Accordion expanded={isOpen} onChange={handleChange}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='block info'
            id='block-info'
            sx={{
              bgcolor: theme.colors.darkTheme.grey.medium,
              borderRadius: theme.borderRadius.primary,
              height: '2.2rem',
              minHeight: '5px',
              pl: '0.8rem',
              pr: '0.8rem',
              '& .Mui-expanded': {
                margin: '0.3rem 0',
                minHeight: '5px',
              },
              '& svg': { color: theme.colors.common.softWhite },
            }}
          >
            <Box sx={{ ...flexCenter, mr: '0.5rem' }}>
              <ListIcon fontSize='small' />
            </Box>
            <Typography
              noWrap
              sx={{
                m: 0,
                mr: '0.5rem',
                mt: '1px',
                color:
                  errors.length > 0
                    ? theme.colors.darkTheme.red
                    : theme.colors.common.white,
              }}
              fontSize='small'
            >
              {t('Block Info', { ns: 'ranch-view' })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: '1rem',
              pt: 0,
              pb: '0.75rem',
              ...flexCenter,
              flexDirection: 'column',
            }}
          >
            {errorsToDisplay}
            {content}
          </AccordionDetails>
        </Accordion>
      </Box>
    )
  );
};

export default MapBlockInfo;

interface AcreDisplayWithLabelProps {
  label: string;
  value: number | null;
  hasError?: boolean;
  isPercentage?: boolean;
}

const AcreDisplayWithLabel: React.FC<AcreDisplayWithLabelProps> = ({
  label,
  value,
  hasError,
  isPercentage,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexDirection: 'row',
      }}
    >
      {value &&
        (isPercentage ? (
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: theme.colors.common.bloomfieldGreen,
              mr: '0.25rem',
            }}
          >{`${roundFractional(value, roundDigits).toString()}%`}</Typography>
        ) : (
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: theme.colors.common.bloomfieldGreen,
              mr: '0.25rem',
            }}
          >{`${roundFractional(value, roundDigits).toString()}`}</Typography>
        ))}
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: hasError
            ? theme.colors.darkTheme.red
            : theme.colors.common.white,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

interface SpacingDisplayWithLabelProps {
  label: string;
  value: number | null;
  hasError?: boolean;
  autounits?: boolean;
}

const SpacingDisplayWithLabel: React.FC<SpacingDisplayWithLabelProps> = ({
  label,
  value,
  hasError,
  autounits = true,
}) => {
  const theme = useTheme();
  const metersUnitSymbol = useUnitSymbol(MeasurementTypes.meterOrFeet);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexDirection: 'row',
      }}
    >
      {value && (
        <Typography
          sx={{
            fontSize: '0.8rem',
            color: theme.colors.common.bloomfieldGreen,
            mr: '0.25rem',
          }}
        >
          {roundFractional(value, roundDigits).toString()}
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: hasError
            ? theme.colors.darkTheme.red
            : theme.colors.common.white,
        }}
      >
        {label}
        {autounits ? ` (${metersUnitSymbol})` : ''}
      </Typography>
    </Box>
  );
};
